import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/Skeleton';

/**
 * §8's loading state: skeletons in `paper-sunken` **shaped like the card** — the header, the three
 * figures of the summary row and one consideration block — never a centred spinner on a full page.
 *
 * ⚠️ **The title is a skeleton and not the word "Candidate", because the title *is* the person's
 * name.** Announcing a placeholder heading and replacing it a moment later is the "layout that jumps
 * when data lands" §8 forbids, and it would be the first thing a screen reader reads on the
 * most-read screen in the product. The state announces itself instead — `aria-busy` and one
 * `sr-only` sentence — which is the same exchange `DashboardSkeleton` makes, recorded in
 * `states.test.tsx` as a named heading exception rather than left as a silent gap.
 */
export function PersonCardSkeleton() {
  const { t } = useTranslation('candidates');

  return (
    <div className="max-w-3xl" aria-busy="true">
      <span className="sr-only">{t('card.loading')}</span>

      <div className="flex items-start gap-4">
        <Skeleton className="size-12 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="mt-2 h-4 w-72" />
        </div>
      </div>

      <div className="mt-8">
        <Skeleton className="h-4 w-24" />
        <div className="mt-4 flex flex-col gap-4 border-y border-rule py-4 lg:flex-row lg:gap-10">
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-28" />
        </div>
      </div>

      <div className="mt-10">
        <Skeleton className="h-4 w-32" />
        <div className="mt-4 rounded border border-rule p-4">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="mt-2 h-3 w-32" />
          <div className="mt-4 space-y-px border-y border-rule">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
